<script setup lang="ts">
import { ElCollapse, type FormRules } from "element-plus";
import { ref, computed, watch, type Ref } from "vue";
import draggable from "vuedraggable";
import ExpandableBoxItem, {
  type ExpandableBoxListItem,
} from "./ExpandableBoxItem.vue";
import type { MoveEvent } from "@/types/utility";

export interface Props {
  list: ExpandableBoxListItem[];
  disabled?: boolean;
  disableToggle?: boolean;
  disableOrdering?: boolean;
  formRules?: FormRules;
  accordion?: boolean;
  hideTag?: boolean;
  characterLimit?: number;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update", payload: ExpandableBoxListItem): void;
  (e: "editing-title", payload: boolean): void;
  (
    e: "update-order",
    {
      list,
      id,
      newIndex,
    }: { list: ExpandableBoxListItem[]; id: string; newIndex: number }
  ): void;
  (e: "has-expanded", value: boolean): void;
}>();

const expandedPanels = ref<string[]>([]);
const changeableList = computed(() => [...props.list]);
const drag = ref(false);

const boxItemRefs: Record<string, Ref | never> = {};
defineExpose({
  boxItemRefs,
});

watch(
  changeableList,
  (list) => {
    list.forEach((element) => {
      boxItemRefs[`item${element.id}`] = ref();
    });
  },
  { immediate: true }
);

const updateOrder = ({ newIndex }: { newIndex: number }) => {
  changeableList.value[newIndex].order = props.list[newIndex].order;

  emit("update-order", {
    list: changeableList.value,
    id: changeableList.value[newIndex].id,
    newIndex,
  });
};

const isValidMove = (event: MoveEvent) => {
  const { index, futureIndex } = event.draggedContext;
  return (
    changeableList.value[index].order !== undefined &&
    changeableList.value[futureIndex].order !== undefined
  );
};

const changeActive = (event: boolean, element: ExpandableBoxListItem) => {
  emit("update", { ...element, active: event });
  expandedPanels.value = [];
};
</script>

<template>
  <el-collapse
    v-model="expandedPanels"
    v-cy="'expandableBox'"
    class="expandable-box-collapse"
    :accordion="accordion"
  >
    <draggable
      :list="changeableList"
      handle=".handle-draggable"
      drag-class="draggable-drag"
      item-key="id"
      :move="isValidMove"
      :animation="300"
      @start="drag = true"
      @end="drag = false"
      @update="updateOrder"
    >
      <template #item="{ element }">
        <ExpandableBoxItem
          :id="element.id"
          :ref="boxItemRefs[`item${element.id}`]"
          v-cy="element.title"
          :form-rules="formRules"
          :disable-title-editing="element.disableTitleEditing ?? false"
          :title="element.title"
          :tag="!hideTag && element?.tag"
          :active="element?.active"
          :disabled="disabled"
          :disable-toggle="disableToggle || element.disableToggle"
          :disable-ordering="disableOrdering"
          :disabled-expand="element.disabledExpand"
          :orderable="element.order !== undefined"
          :uneditable="element?.uneditable"
          :expanded="expandedPanels?.includes(element.id)"
          :is-dragging="drag"
          :is-azure-ad-synced="element.synced"
          :is-custom-field="element.numericId !== -1"
          :character-limit="characterLimit"
          @change-title="
            (event) => emit('update', { ...element, title: event })
          "
          @editing-title="(event) => emit('editing-title', event)"
          @change-active="changeActive($event, element)"
          @has-expanded="(event) => emit('has-expanded', event)"
        >
          <slot :list-item="element" />

          <template #subAction>
            <slot
              name="subAction"
              :list-item="element"
            />
          </template>
        </ExpandableBoxItem>
      </template>
    </draggable>
  </el-collapse>
</template>

<style lang="css" scoped>
.expandable-box-collapse {
  border-right: 1px solid var(--so-collapse-border-color);
  border-left: 1px solid var(--so-collapse-border-color);

  --so-collapse-header-height: 60px;
}

.draggable-drag {
  background-color: var(--so-draggable-bg-color);
}
</style>
